import { action } from '@ember/object';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { allowTransitionIfClean } from 'bcl/misc/transition';
import type OrderEntryModel from 'bcl/models/order-entry';
import type SupplierModel from 'bcl/models/supplier';
import type SupplierItemModel from 'bcl/models/supplier-item';
import type SessionService from 'bcl/services/session';
import type Store from '@ember-data/store';
import type ItemModel from 'bcl/models/item';

export default class AuthOrdersNewRoute extends Route {
  @service declare store: Store;
  @service declare session: SessionService;
  @service declare router: RouterService;

  model() {
    const kind = this.session.orderKind;
    this.session.orderKind = undefined;

    const suplID = this.session.orderSuplID;
    this.session.orderSuplID = undefined;

    if (!kind || !suplID) {
      alert(
        'Die Art der Bestellung oder die Lieferanten-ID waren nicht richtig angegeben.'
      );
      return this.router.transitionTo('auth.orders.index');
    }

    switch (kind) {
      case 'bestand':
        return this.createOrderBestand(suplID);
      case 'standard':
        return this.createOrderStandard(suplID);
      case 'frei':
        return this.createOrderFrei();
      case 'special':
        return this.createOrderSpecial();
      default:
        alert('Kenne diesen Typ der Bestellung nicht');
    }
  }
  @action
  createOrderStandard(supl_id: string) {
    const supplier: SupplierModel = this.store.peekRecord('supplier', supl_id);
    const suplItems: SupplierItemModel[] = supplier.supplierItems.filter(
      (item) => !!item.standard_order
    );
    const orderEntries: OrderEntryModel[] = suplItems.map((suplItem, idx) => {
      return this.store.createRecord('order-entry', {
        supplier_item: suplItem,
        amount: suplItem.standard_order,
        position: idx,
      });
    });
    return this.store.createRecord('order', {
      delivery_date: this._nextDelieveryDate,
      supplier: supplier,
      orderEntries,
      orderType: 'standard',
    });
  }
  get _nextDelieveryDate() {
    // console.log('a');
    // return '2024-05-06';
    const d = new Date();
    // set to next wednesday, even if today is wednesday
    d.setDate(d.getDate() + ((3 + 7 - d.getDay()) % 7 || 7));
    d.setHours(10);
    return d;
  }
  @action
  createOrderFrei() {
    // filter to first non außerordentliche Lieferant
    const suppliers = this.store.peekAll('supplier');
    const firstNonSpecial = suppliers.find(
      (supl: SupplierModel) => supl.additional !== 'special'
    );
    const order = this.store.createRecord('order', {
      supplier: firstNonSpecial,
      delivery_date: this._nextDelieveryDate,
      orderType: 'frei',
    });
    // add all available supplier-items for that

    const allItems =
      firstNonSpecial?.supplierItems?.map((item) => item.id) ?? [];

    for (let i = 0; i < allItems.length; i++) {
      const suplItem = this.store.peekRecord('supplier-item', allItems[i]);

      order.orderEntries.push(
        this.store.createRecord('order-entry', {
          supplier_item: suplItem,
          amount: 0,
          position: i,
        })
      );
    }
    return order;
  }
  @action
  createOrderBestand(supl_id?: string) {
    // fehlmenge für alle items an allen standorten ermitteln
    const allItems = this.store.peekAll('item') as ItemModel[];
    const itemsNeedRefill = allItems
      .filter((item) => item.active)
      .map((item) => {
        const totalActual = item.locationItems.reduce(
          (sum, locItem) => sum + locItem.virtual_calculated_amount,
          0
        );
        const totalOrderedNotArrived =
          item.orderedAndNotArrivedAmount * item.container_size;

        return [
          item,
          Math.round(
            (item.target_amount - totalActual - totalOrderedNotArrived) /
              item.container_size
          ),
        ];
      })
      .filter(([_, need]) => need > 0) as [string, number][];

    const choosenSupplier: SupplierModel = this.store.peekRecord(
      'supplier',
      supl_id
    );

    const itemsFromSupplier = itemsNeedRefill
      .map(([item, amount]) => [
        amount,
        item.supplierItems.filter(
          (supItem: SupplierItemModel) => supItem.supplier === choosenSupplier
        )?.[0],
      ])
      .filter(([_, supItem]) => supItem);

    const orderEntries: OrderEntryModel[] = itemsFromSupplier.map(
      ([amount, suplItem], idx) => {
        return this.store.createRecord('order-entry', {
          supplier_item: suplItem,
          amount: amount,
          position: idx,
        });
      }
    );

    return this.store.createRecord('order', {
      delivery_date: this._nextDelieveryDate,
      supplier: choosenSupplier,
      orderEntries,
      orderType: 'bestand',
    });
  }
  @action
  async createOrderSpecial() {
    // find special supplier
    const specialSupplier: SupplierModel = this.store
      .peekAll('supplier')
      .find(
        (supplier) => (supplier as SupplierModel).additional === 'special'
      ) as SupplierModel;
    if (!specialSupplier) {
      alert(
        'Es gibt keinen Lieferanten, der als "Sonderlieferant" markiert ist. Bitte wende dich an den Administrator. FehlerCode: BCL-410'
      );
      return this.router.transitionTo('auth.orders.index');
    }
    // create supplier items for special supplier for all items
    const allItems = this.store.peekAll('item');
    const suplItems: SupplierItemModel[] = allItems
      .filter((item) => item.active)
      .map((item) => {
        // if not existing, create new supplier item
        const existing = item.supplierItems.find(
          (suplItem) => suplItem.supplier === specialSupplier
        ) as SupplierItemModel | undefined;
        if (existing) {
          return existing;
        }
        return this.store.createRecord('supplier-item', {
          supplier: specialSupplier,
          item,
          price: 0,
          standard_order: 0,
        });
      });
    await Promise.all(
      suplItems.filter((item) => item.isNew).map((item) => item.save())
    );
    const orderEntries: OrderEntryModel[] = suplItems.map((suplItem, idx) => {
      return this.store.createRecord('order-entry', {
        supplier_item: suplItem,
        amount: 0, // default amount is 0, can be changed later
        position: idx,
      });
    });
    // create order with special supplier
    return this.store.createRecord('order', {
      delivery_date: new Date(),
      supplier: specialSupplier,
      orderType: 'special',
      status: 'special',
      orderEntries,
    });
  }
  @action
  willTransition(transition: Transition) {
    return allowTransitionIfClean(this, transition);
  }
}
